import { Controller } from "stimulus"
import { formatCurrencyAmount } from "../utils/format_currency_amount"
import { parseAmount } from "../utils/format_number"

// Generic Donation Flow behavior shared by every client -- currency/amount handling, address
// requirements, UK Gift Aid, and the core review summary rows. A client-specific controller
// (e.g. happier_lives_institute_donation_controller.js, one_for_the_world_donation_controller.js)
// stays attached alongside this one on the same wrapper element and delegates here via
// sharedController, adding only what's genuinely unique to that client (WELLBY impact calculator,
// pledge percentage/income, etc.) instead of re-implementing this.
//
// Deliberately not self-wiring to donation-wizard:pageShown/validate: each client controller calls
// into these methods from its own handlers, so one client can extend or skip a step (e.g. OFTW has
// no in-page one-time/recurrent toggle) without this controller assuming what every client needs.
export default class extends Controller {
  static targets = [
    "currencySelect", "amountCurrencySymbol", "amountDollars", "amountCents", "amountNote", "presetAmount", "presetAmountGroup", "customAmountBtn",
    "paymentMethodGroup", "donorQuestion", "telephoneField",
    "countrySelect", "giftAidCard", "giftAidCheckbox", "giftAidTitle", "giftAidTitleOptionalTag", "giftAidTitleRequiredTag",
    "stateInput", "stateOptionalTag", "stateRequiredTag", "postalCodeInput", "postalOptionalTag", "postalRequiredTag",
    "addressLine1Note", "postalCodeNote",
    "firstName", "lastName", "email", "addressLine1", "addressLine2", "city",
    "summaryName", "summaryEmail", "summaryAddressRow", "summaryAddress", "summaryPortfolio", "summaryAmount",
    "summaryGiftAidRow", "summaryTipRow", "summaryTip"
  ]

  static values = {
    // "pledge" or "donation" (see Flows::Pages.pledge?), for donor-facing copy. Defaulted where
    // read -- this app's Stimulus version doesn't support { type, default } value objects.
    giftNoun: String,
    minimumContributionAmount: Number
  }

  // A fresh page load never fires currencySelected()/countrySelected() -- without this, HLI's
  // pre-filled default amount (e.g. "$50") looks complete but leaves the hidden amount_cents
  // field empty until the donor touches the amount or currency field, since validateAmount() only
  // checks amount_dollars while donation-flow-payment reads amount_cents at Review. Also stamps
  // address requirements/Gift Aid for a pre-filled country (e.g. a BACS-return donor).
  initialize() {
    this.updateAmountCents()
    this.currencySelected()
    this.countrySelected()
  }

  // Toggle a step field's error state and its adjacent .donation-flow__field-note.
  markFieldValidity(inputTarget, noteTarget, valid, message) {
    inputTarget.classList.toggle("is-danger", !valid)
    if (noteTarget) {
      noteTarget.textContent = valid ? "" : message
      noteTarget.classList.toggle("is-hidden", valid)
      noteTarget.classList.remove("donation-flow__field-note--hint")
    }
    return valid
  }

  validateAmount() {
    if (!this.hasAmountDollarsTarget) return true

    const minDollars = this.minimumContributionAmountValue
    const dollars = this.amountDollars
    const symbol = this.hasAmountCurrencySymbolTarget ? this.amountCurrencySymbolTarget.textContent : ""
    const minCents = Math.round(minDollars * this.centsMultiplier)
    const minimum = formatCurrencyAmount(minCents, symbol, this.isZeroDecimalCurrency, { stripWholeCents: true })

    return this.markFieldValidity(
      this.amountDollarsTarget,
      this.hasAmountNoteTarget ? this.amountNoteTarget : null,
      !isNaN(dollars) && dollars >= minDollars,
      this.amountError(`The minimum ${this.giftNounValue || "donation"} is ${minimum}.`)
    )
  }

  // The typed amount, strictly parsed (see parseAmount) -- NaN for anything that isn't plainly a number.
  get amountDollars() {
    return this.hasAmountDollarsTarget ? parseAmount(this.amountDollarsTarget.value) : NaN
  }

  // Something typed that isn't a number gets told so, rather than "the minimum is $5".
  amountError(minimumMessage) {
    const typed = this.hasAmountDollarsTarget && this.amountDollarsTarget.value.trim() !== ""
    return typed && isNaN(this.amountDollars) ? "Enter an amount like 1,000 or 25.50." : minimumMessage
  }

  // Same UK postcode check OFTW's legacy pledge checkout runs client-side (oftw_wizard_controller.js)
  // and the Donor model enforces server-side.
  validatePostcode() {
    if (!this.hasPostalCodeInputTarget) return true

    const value = this.postalCodeInputTarget.value.trim()
    const isUk = this.hasCountrySelectTarget && this.countrySelectTarget.value === "United Kingdom"
    const giftAid = this.hasGiftAidCheckboxTarget && this.giftAidCheckboxTarget.checked
    // Empty is handled by the wizard's own required-field check (set for GB in updateAddressRequirements).
    if (!isUk || !giftAid || value === "") return true

    return this.markFieldValidity(
      this.postalCodeInputTarget,
      this.hasPostalCodeNoteTarget ? this.postalCodeNoteTarget : null,
      /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? [0-9][A-Za-z]{2}|[Gg][Ii][Rr] 0[Aa]{2})$/.test(value),
      "Please enter a valid UK postcode, e.g. AA1 3DD"
    )
  }

  currencySelected() {
    if (!this.hasCurrencySelectTarget) return

    const selectedOption = this.currencySelectTarget.selectedOptions[0]
    const symbol = selectedOption?.dataset.symbol || "$"
    if (this.hasAmountCurrencySymbolTarget) {
      this.amountCurrencySymbolTarget.textContent = symbol
      this.amountCurrencySymbolTarget.dataset.zeroDecimal = selectedOption?.dataset.zeroDecimal || "false"
      if (selectedOption?.dataset.unitsPerUsd) {
        this.amountCurrencySymbolTarget.dataset.unitsPerUsd = selectedOption.dataset.unitsPerUsd
      } else {
        delete this.amountCurrencySymbolTarget.dataset.unitsPerUsd
      }
    }
    this.updateAmountCents()

    if (this.wizardController) this.wizardController.currencyCodeValue = this.currencySelectTarget.value
    this.updatePaymentMethodVisibility(this.currencySelectTarget.value)
    this.updateDonorQuestionVisibility(this.currencySelectTarget.value)
    this.telephoneFieldTargets.forEach((field) => { field.dataset.telephoneFieldCurrencyCodeValue = this.currencySelectTarget.value })
  }

  get wizardController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "donation-wizard")
  }

  updatePaymentMethodVisibility(currencyCode) {
    this.paymentMethodGroupTargets.forEach((group) => {
      group.classList.toggle("is-hidden", group.dataset.currency !== currencyCode)
    })
  }

  // A donor question limited to some currencies (data-currencies, see donation_flows/_donor_question)
  // is shown, and its fields submitted, only while one of them is selected -- several questions
  // can share a field name, one per region (e.g. each region's chapter list).
  updateDonorQuestionVisibility(currencyCode) {
    const selected = (currencyCode || "").toUpperCase()
    this.donorQuestionTargets.forEach((question) => {
      const currencies = (question.dataset.currencies || "").split(",").filter(Boolean)
      const asked = currencies.length === 0 || currencies.includes(selected)
      question.classList.toggle("is-hidden", !asked)
      question.querySelectorAll("input, select, textarea").forEach((field) => { field.disabled = !asked })
    })
  }

  // Stripe's zero-decimal currencies (JPY, KRW, VND, ...) have no minor unit
  get isZeroDecimalCurrency() {
    return this.hasAmountCurrencySymbolTarget && this.amountCurrencySymbolTarget.dataset.zeroDecimal === "true"
  }

  get centsMultiplier() {
    return this.isZeroDecimalCurrency ? 1 : 100
  }

  updateAmountCents() {
    if (!this.hasAmountDollarsTarget || !this.hasAmountCentsTarget) return

    const dollars = this.amountDollars
    this.amountCentsTarget.value = isNaN(dollars) ? "" : Math.round(dollars * this.centsMultiplier)
    this.element.dispatchEvent(new CustomEvent("donation-flow-shared:amountChanged", { bubbles: true }))
  }

  // Typing a custom amount clears any preset chip selection and highlights "Custom" instead.
  manualAmountEntered() {
    this.updateAmountCents()
    if (this.hasPresetAmountTarget) this.presetAmountTargets.forEach((btn) => btn.classList.remove("is-active"))
    if (this.hasCustomAmountBtnTarget) this.customAmountBtnTarget.classList.add("is-active")
  }

  selectPresetAmount(event) {
    this.amountDollarsTarget.value = event.currentTarget.dataset.amount
    this.updateAmountCents()
    this.presetAmountTargets.forEach((btn) => btn.classList.toggle("is-active", btn === event.currentTarget))
    if (this.hasCustomAmountBtnTarget) this.customAmountBtnTarget.classList.remove("is-active")
  }

  // "Custom" isn't a value to pick — it hands focus straight to the input, since typing there IS the custom-amount action.
  focusCustomAmount() {
    if (!this.hasAmountDollarsTarget) return
    this.amountDollarsTarget.focus()
    this.amountDollarsTarget.select()
    this.presetAmountTargets.forEach((btn) => btn.classList.remove("is-active"))
    if (this.hasCustomAmountBtnTarget) this.customAmountBtnTarget.classList.add("is-active")
  }

  countrySelected() {
    if (!this.hasCountrySelectTarget) return

    const country = this.countrySelectTarget.value
    const isUk = country === "United Kingdom"
    if (this.hasGiftAidCardTarget) this.giftAidCardTarget.classList.toggle("is-hidden", !isUk)
    this.updateAddressRequirements(country)
    this.giftAidToggled()
  }

  // Snaps the field to the dropdown's exact casing on a case-insensitive match, or clears it if
  // there's no match at all, letting the existing required-field check catch it like any other
  // empty field instead of silently submitting an arbitrary string as the donor's country.
  validateCountry() {
    if (!this.hasCountrySelectTarget) return

    const value = this.countrySelectTarget.value.trim()
    if (value === "") return

    const options = this.element.querySelectorAll('[data-filterable-dropdown-target="item"]')
    const match = Array.from(options).find((item) => item.textContent.trim().toUpperCase() === value.toUpperCase())
    this.countrySelectTarget.value = match ? match.textContent.trim() : ""
    this.countrySelected()
  }

  // Donor#title is only required when uk_gift_aid_accepted -- matches that exactly, not just country === "United Kingdom"
  giftAidToggled() {
    if (!this.hasGiftAidTitleTarget) return

    const required = this.hasGiftAidCheckboxTarget && this.giftAidCheckboxTarget.checked
    this.toggleFieldRequirement(this.giftAidTitleTarget, this.giftAidTitleOptionalTagTarget, this.giftAidTitleRequiredTagTarget, required)
  }

  // Postal code required for GB/US/CA, state/province only for US/CA. Sets/clears
  // data-donation-wizard-target since required_field_* is the generic wizard's validation.
  updateAddressRequirements(country) {
    if (!this.hasStateInputTarget || !this.hasPostalCodeInputTarget) return

    const requiresState = country === "United States" || country === "Canada"
    const requiresPostalCode = ["United Kingdom", "United States", "Canada"].includes(country)

    this.toggleFieldRequirement(this.stateInputTarget, this.stateOptionalTagTarget, this.stateRequiredTagTarget, requiresState)
    this.toggleFieldRequirement(this.postalCodeInputTarget, this.postalOptionalTagTarget, this.postalRequiredTagTarget, requiresPostalCode)

    if (this.hasAddressLine1NoteTarget) this.addressLine1NoteTarget.classList.toggle("is-hidden", country !== "United Kingdom")

    if (this.hasPostalCodeNoteTarget) {
      const postalFormatNotes = {
        "United States": "Format: 5 digits, e.g. 94103 (or 94103-1234).",
        Canada: "Format: A1A 1A1."
      }
      const note = postalFormatNotes[country]
      this.postalCodeNoteTarget.textContent = note || ""
      this.postalCodeNoteTarget.classList.toggle("is-hidden", !note)
      this.postalCodeNoteTarget.classList.add("donation-flow__field-note--hint")
    }
  }

  toggleFieldRequirement(input, optionalTag, requiredTag, required) {
    if (required) {
      input.setAttribute("data-donation-wizard-target", "required_field_personal_info")
    } else {
      input.removeAttribute("data-donation-wizard-target")
      input.classList.remove("is-danger")
    }
    optionalTag.classList.toggle("is-hidden", required)
    requiredTag.classList.toggle("is-hidden", !required)
  }

  // The core rows every client's review step needs. A client controller can populate additional
  // rows of its own (charity splits, estimated impact, ...) alongside this.
  updateReviewSummaryCore() {
    if (this.hasSummaryNameTarget && this.hasFirstNameTarget && this.hasLastNameTarget) {
      this.summaryNameTarget.textContent = `${this.firstNameTarget.value} ${this.lastNameTarget.value}`.trim()
    }
    if (this.hasSummaryEmailTarget && this.hasEmailTarget) {
      this.summaryEmailTarget.textContent = this.emailTarget.value
    }
    if (this.hasSummaryAddressRowTarget && this.hasSummaryAddressTarget) {
      const parts = [
        this.hasAddressLine1Target ? this.addressLine1Target.value : "",
        this.hasAddressLine2Target ? this.addressLine2Target.value : "",
        this.hasCityTarget ? this.cityTarget.value : "",
        this.hasStateInputTarget ? this.stateInputTarget.value : "",
        this.hasPostalCodeInputTarget ? this.postalCodeInputTarget.value : "",
        this.hasCountrySelectTarget ? this.countrySelectTarget.value : ""
      ].filter((part) => part && part.trim())

      this.summaryAddressTarget.textContent = parts.join(", ")
      this.summaryAddressRowTarget.classList.toggle("is-hidden", parts.length === 0)
    }
    if (this.hasSummaryPortfolioTarget) {
      const selectedCard = this.element.querySelector("[data-radio-select-value].is-active")
      this.summaryPortfolioTarget.textContent = selectedCard?.querySelector(".donation-flow__option-card-title")?.textContent || ""
    }

    const symbol = this.hasAmountCurrencySymbolTarget ? this.amountCurrencySymbolTarget.textContent : ""
    const donationCents = Math.round((this.amountDollars || 0) * this.centsMultiplier)
    const tipCents = this.currentTipCents()

    if (this.hasSummaryTipRowTarget) {
      this.summaryTipRowTarget.classList.toggle("is-hidden", tipCents <= 0)
      if (tipCents > 0 && this.hasSummaryTipTarget) {
        this.summaryTipTarget.textContent = formatCurrencyAmount(tipCents, symbol, this.isZeroDecimalCurrency)
      }
    }
    if (this.hasSummaryAmountTarget && this.hasAmountDollarsTarget) {
      this.summaryAmountTarget.textContent = formatCurrencyAmount(donationCents + tipCents, symbol, this.isZeroDecimalCurrency)
    }
    if (this.hasSummaryGiftAidRowTarget) {
      const giftAidClaimed = this.hasGiftAidCheckboxTarget && this.giftAidCheckboxTarget.checked
      this.summaryGiftAidRowTarget.classList.toggle("is-hidden", !giftAidClaimed)
    }
  }

  // Any additive tip mechanism on the page (e.g. hli-tip) -- generic by design, since the tip
  // destination is a client config value (Flows::ClientConfig.tip_destination_organization_ein),
  // not something specific to any one client.
  currentTipCents() {
    const element = this.element.querySelector('[data-controller~="hli-tip"]')
    if (!element) return 0

    const controller = this.application.getControllerForElementAndIdentifier(element, "hli-tip")
    return controller?.tipCents || 0
  }
}
